import { useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// Definição de módulos disponíveis no sistema
export const ALL_MODULES = [
  'okrs',
  'projetos',
  'relatorios',
  'auditoria',
  'cronogramas',
  'organograma',
  'calendario',
  'fluxogramas',
  'treinamentos',
  'gestao-atividades',
] as const;

export type ModuleKey = (typeof ALL_MODULES)[number];

export interface ModuleDefinition {
  key: ModuleKey;
  label: string;
  description: string;
  icon: string;
}

export function usePermissions() {
  const { user } = useAuth();

  const hasPermission = useCallback(
    (moduleKey: ModuleKey | string) => {
      // Superadmin tem acesso a tudo
      if (user?.tipo === 'superadmin') return true;
      if (!user?.modulos) return false;
      return user.modulos.includes(moduleKey);
    },
    [user],
  );

  /**
   * Estritamente checa se a empresa ativa habilitou esse módulo.
   * Diferente de `hasPermission`, NÃO concede bypass para superadmin —
   * usado para esconder no menu módulos opt-in que a empresa selecionada não usa.
   */
  const isModuleEnabled = useCallback(
    (moduleKey: ModuleKey | string) => {
      if (!user?.modulos) return false;
      return user.modulos.includes(moduleKey);
    },
    [user],
  );

  /**
   * O módulo está habilitado em ALGUMA empresa da pessoa (não só na ativa)? Usado pela agenda
   * única (Calendário), que junta as empresas em que a pessoa tem vínculo.
   */
  const hasPermissionInAnyEmpresa = useCallback(
    (moduleKey: ModuleKey | string) => {
      if (user?.tipo === 'superadmin') return true;
      if (user?.modulos?.includes(moduleKey)) return true;
      // Empresa inativa não libera módulo (o backend da agenda também a ignora).
      return !!user?.empresas?.some((empresa) => empresa.status !== 'inativa' && empresa.modulos?.includes(moduleKey));
    },
    [user],
  );

  const canAccessModule = hasPermission;

  return {
    hasPermission,
    hasPermissionInAnyEmpresa,
    isModuleEnabled,
    canAccessModule,
    modulos: user?.modulos || [],
    isSuperAdmin: user?.tipo === 'superadmin',
  };
}
